/**
 * segmenter — where to cut a dictation, decided by the speaker's own pauses.
 *
 * WHY (Miles, 2026-09-09): "speak, pause, it lands", and dictation "keeps
 * occurring and landing until user clicks again the second time or sends the
 * message". So the microphone stays open and this decides, from the sound
 * alone, when a piece of speech is finished and ready to be written down.
 *
 * Cutting on a PAUSE rather than on a timer is what makes the feature feel
 * right AND what makes it accurate. A pause is a sentence boundary, so each
 * piece the transcriber sees is a whole thought with its own punctuation; a
 * fixed-length cut would slice mid-word and hand it half a clause twice.
 *
 * Pure and frame-driven: the only input is the loudness of each audio frame and
 * the clock. Every rule below is a table in the unit test, because these are
 * the numbers somebody will feel — too short a pause and it fires mid-sentence,
 * too long and they wait, staring.
 */

/** Loudness (RMS, 0..1) above which a frame counts as speech.
 *  Chosen for a room, not a studio: browsers apply noise suppression and gain
 *  control before we see the samples, so ordinary speech sits well above this
 *  and a quiet room sits well below. */
export const SPEECH_RMS_THRESHOLD = 0.014;

/** Silence after speech that ends a segment. Long enough to survive the gap
 *  between words and a breath mid-sentence; short enough that the words land
 *  while the speaker is drawing breath for the next one. */
export const PAUSE_MS = 700;

/** A segment is cut here even mid-flow, so somebody who never pauses still sees
 *  their words appear — and so no single request carries minutes of audio. */
export const MAX_SEGMENT_MS = 15_000;

/** Below this there is nothing worth a round trip: a cough, a door. */
export const MIN_SEGMENT_MS = 350;

/** No speech at all for this long and the microphone closes itself, so a
 *  forgotten open mic is not left listening to an empty room. */
export const IDLE_STOP_MS = 30_000;

/** One dictation session's hard ceiling, whatever happens. */
export const MAX_SESSION_MS = 5 * 60_000;

export type SegmenterAction =
  /** Keep listening; nothing to do. */
  | { kind: 'continue' }
  /** Cut here: the frames so far are a whole piece of speech. */
  | { kind: 'cut'; reason: 'pause' | 'max-length' }
  /** Close the microphone. */
  | { kind: 'stop'; reason: 'idle' | 'session-limit' };

export interface SegmenterState {
  /** When the session started (ms). */
  startedAt: number;
  /** When the current segment's first frame arrived, or null between segments. */
  segmentStartedAt: number | null;
  /** The last frame that counted as speech, or null if none yet this segment. */
  lastSpeechAt: number | null;
  /** The last frame that counted as speech in the SESSION — the idle clock. */
  lastSpeechEver: number | null;
  /** True once the current segment has heard any speech at all. */
  heardSpeech: boolean;
}

export function newSegmenterState(now: number): SegmenterState {
  return {
    startedAt: now,
    segmentStartedAt: null,
    lastSpeechAt: null,
    lastSpeechEver: null,
    heardSpeech: false,
  };
}

/** Root-mean-square loudness of one frame of PCM, in 0..1. */
export function frameRms(samples: Float32Array): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) sum += samples[i] * samples[i];
  return Math.sqrt(sum / samples.length);
}

/**
 * One frame in, one decision out. The caller owns the buffer; this owns WHEN.
 *
 * Order matters and is deliberate: the session ceiling and the idle close come
 * first (they end the session whatever else is true), then the max-length cut,
 * then the pause. A pause is only a cut if the segment actually heard speech —
 * otherwise a quiet room would emit an endless run of empty segments, each one
 * a model call for nothing.
 */
export function judgeFrame(
  state: SegmenterState,
  rms: number,
  now: number,
): { next: SegmenterState; action: SegmenterAction } {
  if (now - state.startedAt >= MAX_SESSION_MS) {
    return { next: state, action: { kind: 'stop', reason: 'session-limit' } };
  }

  const isSpeech = rms >= SPEECH_RMS_THRESHOLD;
  const next: SegmenterState = { ...state };

  if (isSpeech) {
    if (next.segmentStartedAt === null) next.segmentStartedAt = now;
    next.lastSpeechAt = now;
    next.lastSpeechEver = now;
    next.heardSpeech = true;
  } else if (next.segmentStartedAt !== null && !next.heardSpeech) {
    // Frames were being kept but none of them was speech — nothing to cut.
    next.segmentStartedAt = null;
  }

  // The idle close is measured from the last speech in the SESSION, so it never
  // fires while somebody is talking with ordinary gaps.
  const quietSince = next.lastSpeechEver ?? next.startedAt;
  if (!isSpeech && now - quietSince >= IDLE_STOP_MS) {
    return { next, action: { kind: 'stop', reason: 'idle' } };
  }

  if (next.heardSpeech && next.segmentStartedAt !== null) {
    if (now - next.segmentStartedAt >= MAX_SEGMENT_MS) {
      return { next: resetSegment(next), action: { kind: 'cut', reason: 'max-length' } };
    }
    if (!isSpeech && next.lastSpeechAt !== null && now - next.lastSpeechAt >= PAUSE_MS) {
      return { next: resetSegment(next), action: { kind: 'cut', reason: 'pause' } };
    }
  }

  return { next, action: { kind: 'continue' } };
}

function resetSegment(state: SegmenterState): SegmenterState {
  return { ...state, segmentStartedAt: null, lastSpeechAt: null, heardSpeech: false };
}

/**
 * Is a finished segment worth sending?
 *
 * A cut that carries less than a moment of audio is a cough or a chair, and
 * sending it costs a model call to be told there were no words in it.
 */
export function isWorthSending(durationMs: number): boolean {
  return durationMs >= MIN_SEGMENT_MS;
}
